import { useEffect, useState } from "react";
import ShopNavBar from '../../components/ShopNavBar'
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SupplierStatsCard from '../../components/supplier/SuplierStatsCard';
import YourLiveListing from '../../components/supplier/YourLiveListing';
import ItemEnquiries from '../../components/supplier/ItemEnquiries';
import { equalTo, get, orderByChild, query } from "firebase/database";
import { enquiriesDbRef } from "../../utils/dbRefs";
import ItemEnquiryShimmer from "../../components/supplier/ItemEnquiryShimmer";
import { useEnquiriesCount } from "../../hooks/useEnquiriesCount";
import { enquiryMarkAsRead } from "../../utils/utils";
import { useSupplierStats } from "../../hooks/useSupplierStats";
import { theme } from "../../utils/theme";

const SupplierDashboard = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [openEnquiryId, setOpenEnquiryId] = useState(null);
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const { totalEnquiriesCount, repliedEnquiriesCount, newEnquiriesCount, loading: enquiriesLoading } = useEnquiriesCount();
    const {
        monthlyGrowth,
        currentMonthCount,
        previousMonthCount,
        loading: growthLoading,
    } = useSupplierStats();
    const t = theme[currentUser?.type || "supplier"];

    const statsData = [
        {
            id: 1,
            value: enquiriesLoading ? 0 : newEnquiriesCount,
            label: "New Enquiries",
            color: "#4a6cf7",
            bgColor: "#f0f4ff",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className="w-[18px] h-[18px]">
                    <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                </svg>
            ),
        },
        {
            id: 2,
            value: enquiriesLoading ? 0 : totalEnquiriesCount,
            label: "Total Enquiries",
            color: "#16a34a",
            bgColor: "#f0fdf4",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-[18px] h-[18px]"
                >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h10"></path>
                    <path d="M17 8h4"></path>
                    <path d="M17 12h4"></path>
                </svg>
            )
        },
        {
            id: 3,
            value: enquiriesLoading ? 0 : repliedEnquiriesCount,
            label: "Replied Enquiries",
            color: "#ff5323",
            bgColor: "#fff5f2",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-[18px] h-[18px]"
                >
                    <polyline points="9 17 4 12 9 7"></polyline>
                    <path d="M20 18v-2a4 4 0 0 0-4-4H4"></path>
                </svg>
            )
        },
        {
            id: 4,
            value: growthLoading
                ? "0%"
                : `${monthlyGrowth > 0 ? "+" : ""}${monthlyGrowth}%`,
            label: "This Month",
            color: "#eab308",
            bgColor: "#fefce8",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className="w-[18px] h-[18px]">
                    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                    <polyline points="16 7 22 7 22 13"></polyline>
                </svg>
            ),
        },
    ];

    useEffect(() => {
        const fetchEnquiries = async () => {
            if (!currentUser?.uid) return;

            setLoading(true);

            try {
                const snapshot = await get(query(enquiriesDbRef, orderByChild("shopId"), equalTo(currentUser.uid)));

                if (!snapshot.exists()) {
                    setEnquiries([]);
                    return;
                }

                const data = snapshot.val();

                const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

                const list = Object.entries(data)
                    .map(([id, value]) => ({
                        id,
                        ...value,
                    }))
                    .filter((item) => {
                        return (
                            (item.isRead === false || item.isReplied === false) &&
                            item.createdAt >= sevenDaysAgo
                        );
                    })
                    .sort((a, b) => b.createdAt - a.createdAt)
                    .slice(0, 3);

                setEnquiries(list);
            } catch (error) {
                console.error("Error loading enquiries:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchEnquiries();
    }, [currentUser]);

    const handleToggleRead = (enquiry) => {
        const isOpening = openEnquiryId !== enquiry.id;

        setOpenEnquiryId(isOpening ? enquiry.id : null);

        if (isOpening && !enquiry.isRead) {
            enquiryMarkAsRead(enquiry.id);
            setEnquiries((prev) =>
                prev.map((e) =>
                    e.id === enquiry.id ? { ...e, isRead: true } : e
                )
            );
        }
    };


    return (
        <div className='bg-background min-h-full flex flex-col font-["Plus_Jakarta_Sans",sans-serif]'>
            <ShopNavBar type={currentUser?.type || "supplier"} />
            <div className='flex-1 max-w-[1200px] mx-auto w-full px-[16px] md:px-[40px] py-[24px] md:py-[32px] pb-[80px] md:pb-[40px]'>
                <div className="mb-[24px]">
                    <h1 className="font-semibold text-[24px] md:text-[28px] text-[#212326] mb-[4px]">
                        Welcome back, {currentUser?.shopName ? currentUser.shopName.split(" ")[0] : "Shop Owner"}!
                    </h1>
                    <p className="font-medium text-[14px] text-[#717182]">
                        Here's an overview of your supplier listing and recent enquiries
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-[12px] mb-[24px]">
                    {statsData.map((item) => (
                        <SupplierStatsCard key={item.id} item={item} />
                    ))}
                </div>

                <YourLiveListing />

                <div className="bg-white rounded-[16px] border border-[#e4e5e7] overflow-hidden">
                    <div className="flex items-center justify-between px-[20px] py-[14px] border-b border-[#f0f1f3]">
                        <p className="font-semibold text-[15px] text-[#212326]">Recent Enquiries</p>
                        <span className="font-medium text-[12px] text-[#9298a0]">Last 7 days</span>
                    </div>
                    <div className="divide-y divide-[#f0f1f3]">
                        <div className="divide-y divide-[#f0f1f3]">

                            {/* LOADING */}
                            {loading &&
                                Array.from({ length: 3 }).map((_, i) => (
                                    <ItemEnquiryShimmer key={i} />
                                ))
                            }

                            {/* DATA */}
                            {!loading && enquiries.length > 0 &&
                                enquiries.map((item) => (
                                    <ItemEnquiries
                                        key={item.id}
                                        item={item}
                                        isOpen={openEnquiryId === item.id}
                                        onToggle={() => handleToggleRead(item)}

                                    />
                                ))
                            }

                            {/* EMPTY STATE */}
                            {!loading && enquiries.length === 0 && (
                                <div className="py-[40px] text-center">
                                    <p className="text-[13px] text-[#9298a0] font-semibold">
                                        No recent enquiries found
                                    </p>
                                </div>
                            )}

                        </div>
                    </div>
                    <div className="px-[20px] py-[12px] border-t border-[#f0f1f3] bg-[#fafafa]">
                        <button
                            onClick={() => navigate("/supplier/enquiries")}
                            className={`flex items-center gap-[4px] font-semibold text-[13px] text-[${t.primary}] hover:underline mx-auto`}
                        >
                            View all enquiries
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-up-right w-[12px] h-[12px]">
                                <path d="M7 7h10v10"></path>
                                <path d="M7 17 17 7"></path>
                            </svg>
                        </button>
                    </div>

                </div>

            </div>
        </div>

    )
}

export default SupplierDashboard;
